import { FC, useEffect, useMemo } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { classes, style, stylesheet } from 'typestyle'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'
import { withRouter } from '../../__aeki__/contexts/with-router'

/* Utils ======================================================================================== */
import { get } from '../../__aeki__/share/find'

/* Components =================================================================================== */
import { HEADER_HEIGHT } from '../../__aeki__/components/app-layout/app-layout.constants'
import AppLayout, { MAX_WIDTH } from './layout'
import Footer from '../__home__/components/footer'
import Skeleton from '../../__aeki__/components/skeleton'
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Childcares } from './components/childcares'
import { Heading } from '../../__aeki__/components/text/heading.component'
import { Info } from '../posts/components/info'
import { Information } from './components/information'
import { Line } from '../../__aeki__/components/line'
import { Loader } from '../../__aeki__/components/loader'
import { Openings } from './components/openings'
import { Text } from '../../__aeki__/components/text/text.component'
import { Title } from '../../__aeki__/components/text/title.component'
import { VisibleFade, VisibleOpacity } from '../__home__/components/visibile'

/* Styles ======================================================================================= */
import { br12i } from '../../__aeki__/styles/styleset/border-radius/br12i'
import { br24i } from '../../__aeki__/styles/styleset/border-radius/br24i'
import { br8i } from '../../__aeki__/styles/styleset/border-radius/br8i'
import { createMediaQuery } from '../../__aeki__/components/style-class/style-class.utils'
import { mb24 } from '../../__aeki__/styles/styleset/margin/mb24'
import { mb4 } from '../../__aeki__/styles/styleset/margin/mb4'
import { mb40 } from '../../__aeki__/styles/styleset/margin/mb40'
import { mb8 } from '../../__aeki__/styles/styleset/margin/mb8'
import { mt4 } from '../../__aeki__/styles/styleset/margin/mt4'
import { paddingVerticalInner, paddingVerticalOuter } from '../../__aeki__/styles/default/padding'
import { pb32 } from '../../__aeki__/styles/styleset/padding/pb32'
import { rowsmi } from '../../__aeki__/styles/styleset/row/rowsmi'
import { textCenter } from '../../__aeki__/styles/styleset/text-center/textCenter'
import { w100smi } from '../../__aeki__/styles/styleset/width/w100smi'
import { w20 } from '../../__aeki__/styles/styleset/width/w20'
import { w50 } from '../../__aeki__/styles/styleset/width/w50'
import { f12i } from '../../__aeki__/styles/styleset/font-size/f12i'
import { pt64 } from '../../__aeki__/styles/styleset/padding/pt64'
import { pb64 } from '../../__aeki__/styles/styleset/padding/pb64'
import { mb12 } from '../../__aeki__/styles/styleset/margin/mb12'

/* <Childcare /> ================================================================================ */
const Tab = (props: { label: any; selected?: boolean; onClick?: any }) => {
  const { label, selected, onClick } = props

  return (
    <Box
      baseline={selected ? 'surface' : 'background'}
      className={classes(paddingVerticalInner, style({ cursor: 'pointer', userSelect: 'none' }))}
      fullHeight
      minHeight={44}
      maxHeight={44}
      alignCenterY
      onClick={onClick}
    >
      <Text baseline="surface" size="sm" semiBold={selected} light={!selected}>
        {label}
      </Text>
    </Box>
  )
}

const DEFAULT_INJECTION = {
  body: {
    allowBookmarks: false,
    body: [],
  },
}

const Community: FC<any> = (props: any) => {
  const { inject, eject } = props
  const injection = inject()
  const history = useNavigate()
  const historyLocation = useLocation()
  const url = `${process.env.REACT_APP_API_URL}${historyLocation.pathname}`
  const [post, $post] = useFetch(Object.keys(injection).length ? injection : DEFAULT_INJECTION)
  const [tabSelected, $tabSelected] = useValue(0)

  const id = get('id', post.payload, 'string')
  const hours = get('body.hours', post.payload, 'object', null)
  const name = get('body.generalInformation.name', post.payload, 'string')
  const type = get('body.generalInformation.communityType', post.payload, 'string')
  const phone = get('body.generalInformation.phoneNumber', post.payload, 'string')
  const serviceArea = get('body.generalInformation.serviceArea', post.payload, 'string')
  const addressLine = get('body.generalInformation.addressLine1', post.payload, 'string')
  const coordinates = get('location.coordinates', post.payload, 'array')
  const isNotAvailable = get('body.availability.isNotAvailable', post.payload, 'boolean')
  const placeId = get('meta.place.body.key', post.payload, 'string')
  const placeName = get('meta.place.body.name', post.payload, 'string')
  const images = get('meta.images', post.payload, 'array')
  const shortDecription = `${name} is located at ${addressLine}. Call ${phone} for more details.`

  useEffect(() => {
    const hash = globalThis?.location?.hash
    const parsedHash = hash.split('#')

    if (parsedHash[1]) {
      switch (parsedHash[1]) {
        case 'openings':
          $tabSelected.set(1)
          break
        case 'child-care-list':
          $tabSelected.set(2)
          break
      }
    }

    return () => {
      eject()
    }
  }, [])

  useEffect(() => {
    if (post.mounted && historyLocation.pathname) {
      if (Object.keys(injection).length === 0) {
        $post.get({ url })
      }
    }
  }, [post.mounted])

  const renderHoursForSchema = () => {
    const toReturn: any = []

    for (const key in hours) {
      if (hours?.[key]?.open && hours?.[key]?.close) {
        let openTime = hours?.[key]?.open
        if (openTime.includes('PM')) {
          const splitted = openTime.split(':')
          const hour = parseInt(splitted[0]) + 12
          const minutes = splitted[1]
          openTime = `${hour}:${minutes}`.replace(/( AM)|( PM)/, '')
        } else {
          openTime = openTime.replace(/( AM)|( PM)/, '')
        }

        let closeTime = hours?.[key]?.close
        if (closeTime.includes('PM')) {
          const splitted = closeTime.split(':')
          const hour = parseInt(splitted[0]) + 12
          const minutes = splitted[1]
          closeTime = `${hour}:${minutes}`.replace(/( AM)|( PM)/, '')
        } else {
          closeTime = closeTime.replace(/( AM)|( PM)/, '')
        }

        toReturn.push(`${key[0].toUpperCase()}${key[1]} ${openTime}-${closeTime}`)
      }
    }

    return toReturn
  }

  const head = useMemo(
    () => ({
      title: `${name} - ChildAt.io`,
      description: shortDecription,
      mainImage: images[0],
      canonical: `${process.env.REACT_APP_WEB_URL}/a/c/${id}`,
      schema: [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: `Childcare in ${placeName}`,
              item: `${process.env.REACT_APP_WEB_URL}/a/childcares/${placeId}`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: name,
              item: `${process.env.REACT_APP_WEB_URL}/a/c/${id}`,
            },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name,
          legalName: name,
          image: images[0] ? images : [],
          address: addressLine,
          geo: {
            '@type': 'GeoCoordinates',
            latitude: coordinates[1],
            longitude: coordinates[0],
          },
          telephone: phone,
          openingHours: renderHoursForSchema(),
          description: shortDecription,
          url: `${process.env.REACT_APP_WEB_URL}/a/c/${id}`,
        },
      ],
    }),
    [name],
  )

  const contentHead = useMemo(() => {
    return (
      <>
        <Box baseline="secondary" className={STYLES.containerHero} row>
          <Box
            baseline="none"
            className={STYLES.containerHeroContent}
            flex
            alignCenterX
            alignCenterY
          >
            <Box
              className={classes(paddingVerticalOuter)}
              baseline="none"
              maxWidth={MAX_WIDTH}
              centerAutoX
              fullWidth
              row
            >
              <VisibleOpacity>
                <Box className={classes(textCenter, rowsmi)} row baseline="none" alignCenterX>
                  <Loader
                    timeout={0}
                    loaded={name !== '-'}
                    skeleton={
                      <Skeleton className={classes(w100smi, w20, br12i, mb4)} height={20} />
                    }
                    component={
                      // <Link to={`/a/childcares/${placeId}`}>
                      <Text baseline="primary" className={mb8} size="lg" tag={'h2'}>
                        {type === 'CCRR'
                          ? 'Childcare Resource & Referral Centre '
                          : 'Community Centre '}
                        for {serviceArea}
                      </Text>
                      // </Link>
                    }
                  />
                  <Loader
                    timeout={0}
                    loaded={name !== '-'}
                    skeleton={
                      <Skeleton className={classes(w100smi, w50, br24i, mt4)} height={70.88} />
                    }
                    component={
                      <Title className={textCenter} size="md" semiBold tag={'h1'} crank>
                        {name}
                      </Title>
                    }
                  />
                </Box>
              </VisibleOpacity>
            </Box>
          </Box>
        </Box>
        <Info maxWidth={MAX_WIDTH} label="Info" labelWidth={60} icon="error" disableUpperLine>
          <Loader
            timeout={0}
            loaded={name !== '-'}
            skeleton={<Skeleton className={br8i} height={18} width={200} />}
            component={
              <>
                <Text size="sm">
                  Childcare Resource and Referral (CCRR) centres make it easier for families to find
                  and access responsive and inclusive quality licensed and registered childcare by
                  providing information, support, resources and referral services to parents and
                  childcare across the province.
                </Text>
              </>
            }
          />
        </Info>
      </>
    )
  }, [placeName, name, isNotAvailable, serviceArea])

  const promotion = useMemo(
    () => (
      <Box baseline="background" className={classes(paddingVerticalOuter, pt64, pb64)}>
        <VisibleFade>
          <Box baseline="none" maxWidth={700} centerAutoX row alignCenterX>
            <Heading size="xs" className={classes(mb12, textCenter)} semiBold crank>
              Would you like to see childcare {name} supports?
            </Heading>
            <Box className={mb24} baseline="none">
              <Heading size="sm">Browse our search tool</Heading>
            </Box>
            <Link to={`/a/childcares?searchText=${addressLine}`}>
              <Button label="Launch Search Tool" baseline="primary" size="md" square />
            </Link>
          </Box>
        </VisibleFade>
      </Box>
    ),
    [type],
  )

  const content = useMemo(() => {
    let innerContent = <></>
    switch (tabSelected) {
      case 0:
        innerContent = <Information post={post} />
        break
      case 1:
        innerContent = name !== '-' ? <Openings post={post} /> : <></>
        break
      case 2:
        innerContent = <Childcares />
    }

    return (
      <>
        {contentHead}
        <Box
          baseline="background"
          sticky
          className={style({
            top: HEADER_HEIGHT - 1,
            zIndex: 2,
          })}
        >
          <Box baseline="none" maxWidth={MAX_WIDTH} centerAutoX fullWidth flex>
            <>
              <Tab
                label={'Information'}
                selected={tabSelected == 0}
                onClick={() => {
                  $tabSelected.set(0)
                  window.history.pushState('', '', '#')
                }}
              />
              <Tab
                label={'Childcare openings'}
                selected={tabSelected == 1}
                onClick={() => {
                  $tabSelected.set(1)
                  window.history.pushState('', '', '#openings')
                }}
              />
              <Tab
                label={
                  <>
                    Search tool{' '}
                    <i className={classes('material-icons-two-tone', f12i)}>open_in_new</i>
                  </>
                }
                selected={tabSelected == 2}
                onClick={() => {
                  history(`/a/childcares?searchText=${addressLine}&openMap=true`)
                }}
              />
            </>
          </Box>
        </Box>
        <Box className={pb32} baseline="surface" row flex>
          <Box baseline="none" fullWidth row>
            {id !== '-' && innerContent}
          </Box>
        </Box>
        <Line />
        {promotion}
        <Line className={mb40} />
        <Footer />
      </>
    )
  }, [name, tabSelected, post])

  return (
    <AppLayout.render
      head={head}
      headerProps={{
        menu: [],
        scrollEffect: true,
        allowToggleEffect: true,
        maxWidth: MAX_WIDTH,
      }}
      content={content}
    />
  )
}

const STYLES = stylesheet({
  containerHero: {
    marginTop: -56,
    paddingTop: 56,
  },
  containerHeroContent: {
    minHeight: 580,
    paddingTop: 162,
    paddingBottom: 160,
    ...createMediaQuery.sm({
      minHeight: 'calc(100vh - 97px)',
    }),
  },
  lineHero: {
    opacity: 0.1,
  },

  containerMainTitle: {
    width: 'calc(100% - 380px)',
  },
  containerHealth: {
    flexWrap: 'wrap',
  },
  containerTeammateInner: {
    $nest: {
      '&:after': {
        content: '""',
        display: 'block',
        paddingBottom: '100%',
      },
    },
  },
  containerTeammateImage: {
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderRadius: '50% !important',
  },
  containerEmpty: {
    flex: 1,
  },
})

export default withRouter((props: any) => {
  return <Community {...props} />
})
